<template>
  <div class="page-container">
    <Sidebar />

    <div class="main-content">
      <div class="page-header">
        <h1>道地药材管理</h1>
        <div class="header-actions">
          <span class="total-count">共 {{ total }} 条记录</span>
          <input type="file" ref="excelInput" accept=".xlsx,.xls" @change="handleExcelUpload" style="display: none" />
          <button class="upload-btn" @click="$refs.excelInput.click()">
            <span>📤</span> 导入Excel
          </button>
          <button class="add-btn" @click="showAddModal = true">
            <span>➕</span> 添加数据
          </button>
        </div>
      </div>

      <div class="filter-bar">
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="搜索药材名..."
          @keyup.enter="handleSearch"
        />
        <select v-model="selectedProvince" class="province-select" @change="handleProvinceChange">
          <option value="">全部省份</option>
          <option v-for="province in provinces" :key="province" :value="province">
            {{ province }}
          </option>
        </select>
        <button class="search-btn" @click="handleSearch">搜索</button>
        <button class="reset-btn" @click="resetFilters">重置</button>
      </div>

      <div class="content-body">
        <div class="loading" v-if="loading">加载中...</div>
        <div class="error" v-else-if="error">{{ error }}</div>

        <table class="data-table" v-else>
          <thead>
            <tr>
              <th>ID</th>
              <th>药材名</th>
              <th>基原</th>
              <th>省份</th>
              <th>产地信息</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in daodiHerbs" :key="item.id">
              <td>{{ item.id }}</td>
              <td class="name-cell">
                <span class="herb-tag">{{ item.herb_name }}</span>
              </td>
              <td class="origin-cell">{{ item.origin || '-' }}</td>
              <td>
                <span class="province-tag">{{ item.province }}</span>
              </td>
              <td class="location-cell">{{ item.region_info || '-' }}</td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td class="actions">
                <button class="edit-btn" @click="handleEdit(item)">编辑</button>
                <button class="delete-btn" @click="handleDelete(item.id)">删除</button>
              </td>
            </tr>
            <tr v-if="daodiHerbs.length === 0">
              <td colspan="7" class="empty">暂无道地药材数据</td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)">上一页</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">下一页</button>
        </div>
      </div>
    </div>

    <!-- 添加数据弹窗 -->
    <div class="modal-overlay" v-if="showAddModal" @click.self="showAddModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h2>添加道地药材数据</h2>
          <button class="close-btn" @click="showAddModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-item">
              <label>药材名称 *</label>
              <input type="text" v-model="addForm.herb_name" required placeholder="请输入药材名称" />
            </div>
            <div class="form-item">
              <label>省份 *</label>
              <select v-model="addForm.province" required>
                <option value="">请选择省份</option>
                <option v-for="province in provinces" :key="province" :value="province">{{ province }}</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>基原</label>
              <textarea v-model="addForm.origin" placeholder="基原信息（可选）" rows="2"></textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>产地信息</label>
              <textarea v-model="addForm.region_info" placeholder="详细产地信息（可选）" rows="3"></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showAddModal = false">取消</button>
          <button class="submit-btn" @click="handleAddData" :disabled="adding">
            {{ adding ? '添加中...' : '确认添加' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 编辑数据弹窗 -->
    <div class="modal-overlay" v-if="showEditModal" @click.self="showEditModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h2>编辑道地药材数据</h2>
          <button class="close-btn" @click="showEditModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-item">
              <label>药材名称 *</label>
              <input type="text" v-model="editForm.herb_name" required placeholder="请输入药材名称" />
            </div>
            <div class="form-item">
              <label>省份 *</label>
              <select v-model="editForm.province" required>
                <option value="">请选择省份</option>
                <option v-for="province in provinces" :key="province" :value="province">{{ province }}</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>基原</label>
              <textarea v-model="editForm.origin" placeholder="基原信息（可选）" rows="2"></textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>产地信息</label>
              <textarea v-model="editForm.region_info" placeholder="详细产地信息（可选）" rows="3"></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showEditModal = false">取消</button>
          <button class="submit-btn" @click="handleEditData" :disabled="editing">
            {{ editing ? '保存中...' : '保存修改' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import { formatDate } from '../utils/dateFormat'
import { getAdminPassword } from '../utils/api'
import axios from 'axios'
import * as XLSX from 'xlsx'

export default {
  name: 'DaodiHerbsList',
  components: { Sidebar },
  data() {
    return {
      daodiHerbs: [],
      loading: true,
      error: '',
      searchQuery: '',
      selectedProvince: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      provinces: [
        '湖北', '湖南', '江西', '浙江', '福建', '广东', '海南', '西藏', '重庆', '四川',
        '贵州', '云南', '广西', '河南', '安徽', '江苏', '山东', '河北', '山西', '陕西',
        '甘肃', '宁夏', '青海', '内蒙古', '黑龙江', '吉林', '新疆', '辽宁'
      ],
      showAddModal: false,
      adding: false,
      addForm: {
        herb_name: '',
        origin: '',
        province: '',
        region_info: ''
      },
      showEditModal: false,
      editing: false,
      uploading: false,
      editForm: {
        id: null,
        herb_name: '',
        origin: '',
        province: '',
        region_info: ''
      }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  mounted() {
    this.fetchDaodiHerbs()
  },
  methods: {
    async fetchDaodiHerbs() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }
        if (this.searchQuery) {
          params.search = this.searchQuery
        }
        if (this.selectedProvince) {
          params.province = this.selectedProvince
        }
        const response = await axios.get('/api/admin/daodi-herbs', {
          params,
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          const data = response.data.data
          this.daodiHerbs = data?.list || []
          this.total = data?.total || 0
        } else {
          this.error = response.data?.msg || '获取数据失败'
        }
      } catch (err) {
        this.error = '获取数据失败，请稍后重试'
        console.error('Error fetching daodi herbs:', err)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchDaodiHerbs()
    },
    handleProvinceChange() {
      this.currentPage = 1
      this.fetchDaodiHerbs()
    },
    resetFilters() {
      this.searchQuery = ''
      this.selectedProvince = ''
      this.currentPage = 1
      this.fetchDaodiHerbs()
    },
    changePage(page) {
      this.currentPage = page
      this.fetchDaodiHerbs()
    },
    formatDate(dateStr) {
      return formatDate(dateStr)
    },
    async handleAddData() {
      if (!this.addForm.herb_name || !this.addForm.province) {
        alert('请填写必填项：药材名称和省份')
        return
      }
      this.adding = true
      try {
        const response = await axios.post('/api/admin/daodi-herbs', this.addForm, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          alert('添加成功！')
          this.showAddModal = false
          this.resetAddForm()
          this.fetchDaodiHerbs()
        } else {
          alert(response.data?.msg || '添加失败')
        }
      } catch (err) {
        console.error('添加失败:', err)
        alert('添加失败，请稍后重试')
      } finally {
        this.adding = false
      }
    },
    resetAddForm() {
      this.addForm = {
        herb_name: '',
        origin: '',
        province: '',
        region_info: ''
      }
    },
    async handleDelete(id) {
      if (!confirm('确定要删除这条记录吗？')) {
        return
      }
      try {
        const response = await axios.delete(`/api/admin/daodi-herbs/${id}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          alert('删除成功！')
          this.fetchDaodiHerbs()
        } else {
          alert(response.data?.msg || '删除失败')
        }
      } catch (err) {
        console.error('删除失败:', err)
        alert('删除失败，请稍后重试')
      }
    },
    handleEdit(item) {
      this.editForm = {
        id: item.id,
        herb_name: item.herb_name,
        origin: item.origin || '',
        province: item.province,
        region_info: item.region_info || ''
      }
      this.showEditModal = true
    },
    async handleEditData() {
      if (!this.editForm.herb_name || !this.editForm.province) {
        alert('请填写必填项：药材名称和省份')
        return
      }
      this.editing = true
      try {
        const response = await axios.put(`/api/admin/daodi-herbs/${this.editForm.id}`, {
          herb_name: this.editForm.herb_name,
          origin: this.editForm.origin,
          province: this.editForm.province,
          region_info: this.editForm.region_info
        }, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          alert('修改成功！')
          this.showEditModal = false
          this.fetchDaodiHerbs()
        } else {
          alert(response.data?.msg || '修改失败')
        }
      } catch (err) {
        console.error('修改失败:', err)
        alert('修改失败，请稍后重试')
      } finally {
        this.editing = false
      }
    },
    async handleExcelUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      this.uploading = true
      try {
        const data = await file.arrayBuffer()
        const workbook = XLSX.read(data, { type: 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 })

        const headers = rows[0] || []
        const herbIdx = headers.findIndex(h => String(h).includes('药材') || String(h).includes('名称'))
        const originIdx = headers.findIndex(h => String(h).includes('基原'))
        const provinceIdx = headers.findIndex(h => String(h).includes('省份') || String(h).includes('省'))
        const regionIdx = headers.findIndex(h => String(h).includes('产地') || String(h).includes('信息'))

        if (herbIdx === -1) {
          alert('Excel格式错误：缺少药材名称列')
          return
        }

        const importData = []
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i]
          if (!row || !row[herbIdx]) continue
          importData.push({
            herb_name: String(row[herbIdx]).trim(),
            origin: originIdx !== -1 && row[originIdx] ? String(row[originIdx]).trim() : '',
            province: provinceIdx !== -1 && row[provinceIdx] ? String(row[provinceIdx]).trim() : '',
            region_info: regionIdx !== -1 && row[regionIdx] ? String(row[regionIdx]).trim() : ''
          })
        }

        if (importData.length === 0) {
          alert('Excel没有有效数据')
          return
        }

        const res = await axios.post('/api/admin/daodi-herbs/batch-import', 
          { data: importData },
          { headers: { 'x-admin-password': getAdminPassword() } }
        )

        if (res.data?.code === 200) {
          alert(`导入成功！共 ${res.data.data?.imported || importData.length} 条（已覆盖旧数据）`)
          this.fetchDaodiHerbs()
        } else {
          alert('导入失败：' + res.data?.msg)
        }
      } catch (err) {
        console.error('Excel解析错误:', err)
        alert('Excel解析失败：' + err.message)
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

.main-content {
  flex: 1;
  margin-left: 260px;
  padding: 24px;
  overflow-y: auto;
  height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  font-size: 26px;
  color: #2c3e50;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 16px;
}

.total-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.add-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.upload-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(17, 153, 142, 0.4);
}

.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  flex: 1;
  min-width: 200px;
  font-size: 14px;
}

.province-select {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  min-width: 150px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.search-btn:hover {
  background: #2980b9;
}

.reset-btn {
  padding: 10px 20px;
  background: #95a5a6;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.reset-btn:hover {
  background: #7f8c8d;
}

.content-body {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.loading,
.error,
.empty {
  text-align: center;
  padding: 50px;
  color: #666;
}

.error {
  color: #e74c3c;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f8f9fa;
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #eee;
  font-size: 13px;
  white-space: nowrap;
}

.data-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  font-size: 13px;
}

.data-table tr:hover {
  background: #f8f9fa;
}

.name-cell {
  font-weight: 500;
  color: #2c3e50;
}

.herb-tag {
  display: inline-block;
  padding: 4px 12px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.origin-cell {
  max-width: 200px;
  word-break: break-word;
}

.location-cell {
  max-width: 300px;
  word-break: break-word;
}

.province-tag {
  display: inline-block;
  padding: 4px 12px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 12px;
}

.actions {
  display: flex;
  gap: 8px;
}

.delete-btn {
  padding: 6px 12px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s ease;
}

.delete-btn:hover {
  background: #c82333;
}

.edit-btn {
  padding: 6px 12px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s ease;
}

.edit-btn:hover {
  background: #218838;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

.pagination button {
  padding: 8px 15px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
}

.pagination button:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #3498db;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.modal-body {
  padding: 24px;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.form-item {
  flex: 1;
}

.form-item.full-width {
  flex: 1 1 100%;
}

.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
}

.form-item input,
.form-item select,
.form-item textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.form-item input:focus,
.form-item select:focus,
.form-item textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-item textarea {
  min-height: 60px;
  resize: vertical;
  font-family: inherit;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

.cancel-btn {
  padding: 10px 20px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s ease;
}

.cancel-btn:hover {
  background: #5a6268;
}

.submit-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>